// The Mountain behind the Compass screen, from real frames of ONE mountain
// (art-masters/v10/mountain/v2/build_frames.py → mountainFrames.ts):
//   - at the side view (pitch = SIDE_PITCH) it shows the 360° spin frame for the current face;
//   - ▲▼ (the camera pitch) step through the frames of one continuous camera rise from
//     the front face to straight overhead, so every angle is the same mountain.
// Each frame is placed so its summit temple sits exactly under the compass hub (anchorY).
import React, { useEffect, useMemo, useState } from 'react';
import { Animated, Image, StyleSheet } from 'react-native';
import { MOUNTAIN_ASPECT, ORBIT, PITCH, type MountainFrame } from './mountainFrames';
import { AERIAL_PITCH, SIDE_PITCH } from './orbit';

/** Which rise frame a pitch value shows (0 = side view → the spin frame). */
export function pitchFrameIndex(p: number): number {
  const f = (p - SIDE_PITCH) / (AERIAL_PITCH - SIDE_PITCH);
  return Math.max(0, Math.min(PITCH.length - 1, Math.round(f * (PITCH.length - 1))));
}

export function MountainBackdrop({
  pitch,
  orbitFrame,
  orbitFrames,
  anchorY,
  liftY,
  lift,
  width,
  height,
}: {
  pitch: Animated.Value;
  /** Spin frame index from useOrbit (0..orbitFrames-1). */
  orbitFrame: number;
  orbitFrames: number;
  anchorY: number;
  /** Compass view: the summit is lowered (and the frame enlarged about it) to this y. */
  liftY?: number;
  /** 0 = placed at anchorY (the Mountain view), 1 = lowered to liftY. */
  lift?: Animated.Value;
  width: number;
  height: number;
}) {
  const [pi, setPi] = useState(() => pitchFrameIndex(Number(JSON.stringify(pitch))));
  useEffect(() => {
    const id = pitch.addListener(({ value }) => setPi(pitchFrameIndex(value)));
    return () => pitch.removeListener(id);
  }, [pitch]);

  // useOrbit counts its own frames; map onto the v2 spin frames.
  const oi = Math.round((orbitFrame / Math.max(1, orbitFrames)) * ORBIT.length) % ORBIT.length;
  const shown: MountainFrame = pi === 0 ? ORBIT[oi] : PITCH[pi];

  // Frame size: wide enough to fill the screen width and to reach the bottom edge
  // (the clouds trail off the bottom, never empty sky below the mountain).
  const W = useMemo(() => {
    const ty = 0.33; // lowest the temple ever sits in a frame
    return Math.max(width, height * 0.76, (height - anchorY) / ((1 - ty) * MOUNTAIN_ASPECT));
  }, [width, height, anchorY]);
  const Hf = W * MOUNTAIN_ASPECT;
  const place = (f: MountainFrame) => ({ left: width / 2 - f.temple[0] * W, top: anchorY - f.temple[1] * Hf, width: W, height: Hf });

  // Lowered summit: enlarge about the summit just enough that the frame still
  // reaches the top edge (the temple sits at least 0.272 down every frame).
  const dy = liftY != null ? liftY - anchorY : 0;
  const liftScale = liftY != null ? Math.max(1, liftY / (0.272 * Hf)) : 1;
  const liftStyle =
    lift && dy
      ? {
          transformOrigin: `${width / 2}px ${anchorY}px`,
          transform: [
            { translateY: lift.interpolate({ inputRange: [0, 1], outputRange: [0, dy] }) },
            { scale: lift.interpolate({ inputRange: [0, 1], outputRange: [1, liftScale] }) },
          ],
        }
      : null;

  return (
    <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, liftStyle]}>
      {/* All frames stay mounted (decoded once); only the current one is visible. */}
      {ORBIT.map((f, i) => (
        <Image key={`o${i}`} source={f.src} resizeMode="cover" style={[styles.frame, place(f), { opacity: shown === f ? 1 : 0 }]} />
      ))}
      {PITCH.map((f, i) => (i === 0 ? null : <Image key={`p${i}`} source={f.src} resizeMode="cover" style={[styles.frame, place(f), { opacity: shown === f ? 1 : 0 }]} />))}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  frame: { position: 'absolute' },
});
